import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { HiChevronDown, HiCheck } from "react-icons/hi";
import { PlanilhaStatus } from "@models/index";
import {
  STATUS_PLANILHA_ORDEM,
  classeStatusPlanilha,
  normalizarStatusPlanilha,
  rotuloStatusPlanilha,
} from "@utils/planilhaStatus";
import "./PlanilhaStatusSelect.css";

export interface PlanilhaStatusSelectProps {
  status?: string | null;
  onChange: (status: PlanilhaStatus) => Promise<void>;
  disabled?: boolean;
}

const MENU_WIDTH = 210;

/**
 * Pílula de status que abre o seletor ao ser clicada, direto na listagem.
 *
 * O menu vai para um portal com posição fixa, como o KebabMenu: a tabela tem
 * `overflow-x: auto`, que recortaria um dropdown posicionado dentro da célula.
 */
export const PlanilhaStatusSelect: React.FC<PlanilhaStatusSelectProps> = ({
  status,
  onChange,
  disabled = false,
}) => {
  const [open, setOpen] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const btnRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const atual = normalizarStatusPlanilha(status);

  const updatePosition = () => {
    const el = btnRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    // Alinha pela esquerda do gatilho, mas sem vazar da janela à direita.
    const left = Math.max(
      8,
      Math.min(rect.left, window.innerWidth - MENU_WIDTH - 8),
    );
    setCoords({ top: rect.bottom + 4, left });
  };

  const toggle = () => {
    if (disabled || salvando) return;
    if (!open) updatePosition();
    setOpen((v) => !v);
  };

  useEffect(() => {
    if (!open) return;

    const handleOutside = (e: MouseEvent) => {
      if (
        menuRef.current?.contains(e.target as Node) ||
        btnRef.current?.contains(e.target as Node)
      ) {
        return;
      }
      setOpen(false);
    };
    const handleClose = () => setOpen(false);
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };

    document.addEventListener("mousedown", handleOutside);
    window.addEventListener("scroll", handleClose, true);
    window.addEventListener("resize", handleClose);
    document.addEventListener("keydown", handleKey);

    return () => {
      document.removeEventListener("mousedown", handleOutside);
      window.removeEventListener("scroll", handleClose, true);
      window.removeEventListener("resize", handleClose);
      document.removeEventListener("keydown", handleKey);
    };
  }, [open]);

  const selecionar = async (novo: PlanilhaStatus) => {
    setOpen(false);
    if (novo === atual) return;

    setSalvando(true);
    try {
      await onChange(novo);
    } finally {
      setSalvando(false);
    }
  };

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        className={`status-badge status-select-trigger ${classeStatusPlanilha(
          atual,
        )}`}
        onClick={toggle}
        disabled={disabled || salvando}
        aria-haspopup="listbox"
        aria-expanded={open}
        title={disabled ? undefined : "Clique para alterar o status"}
      >
        <span>{rotuloStatusPlanilha(atual)}</span>
        {salvando ? (
          <span className="status-select-spinner" aria-hidden />
        ) : (
          !disabled && <HiChevronDown className="status-select-caret" />
        )}
      </button>

      {open &&
        createPortal(
          <div
            ref={menuRef}
            className="status-select-dropdown"
            role="listbox"
            style={{ top: coords.top, left: coords.left, width: MENU_WIDTH }}
          >
            {STATUS_PLANILHA_ORDEM.map((opcao) => (
              <button
                key={opcao}
                type="button"
                role="option"
                aria-selected={opcao === atual}
                className="status-select-item"
                onClick={() => selecionar(opcao)}
              >
                <span
                  className={`status-select-dot ${classeStatusPlanilha(opcao)}`}
                  aria-hidden
                />
                <span className="status-select-item-label">
                  {rotuloStatusPlanilha(opcao)}
                </span>
                {opcao === atual && (
                  <HiCheck className="status-select-check" aria-hidden />
                )}
              </button>
            ))}
          </div>,
          document.body,
        )}
    </>
  );
};

export default PlanilhaStatusSelect;
